import { createFileRoute } from "@tanstack/react-router";
import { useSimpleForm } from "@/hooks/use-simple-form";
import useAppStore from "@/store";
import { Button } from "@/components/ui/button";
import { useTranslate } from "@/hooks/use-translate";
import { LightSelectField, TextAreaField } from "@/components/ui/form";
import { cn, formatNumber, unPick } from "@/lib/utils";
import type { StockBatch } from "@/store/StockBatch";
import { useEffect } from "react";
import { Copy, Plus } from "lucide-react";
import {
  SupplyReturnStatusActions,
  SupplyReturnStatusColors,
  SupplyReturnStatusMap,
  SupplyReturnValidTransitions,
} from "@/components/supply-return/supply-return-meta";
import type { PaymentInfo } from "@/store/FinanceAccount";
import Avatar from "@/components/ui/avatar";
import type { SupplyReturn } from "@/store/SupplyReturn";
import { InfoElement } from "@/components/ui/shared/details";
import { Toast } from "@/components/ui/toast";
import Show from "@/components/ui/show";
import PayableTransaction from "@/components/ui/shared/payable-transaction";
import ProcessPayment from "@/components/ui/shared/process-payment";

export const Route = createFileRoute(
  "/(dasboard)/inventory/supply-returns/$id"
)({
  component: ViewSupplyReturnBlock,
});

function ViewSupplyReturnBlock() {
  const store = useAppStore();
  const { t, formatDate } = useTranslate();
  const supplyReturn = store.supplyReturn.current as SupplyReturn;
  const stock_batch = supplyReturn?.stock_batch as StockBatch;
  const params = Route.useParams();

  const formatAmount = (amount: number) => {
    return `${formatNumber(amount)} ${store.auth?.session?.shop?.currency}`;
  };

  const form = useSimpleForm({
    reason: "",
    status: "pending",
    payments: [],
  });

  useEffect(() => {
    store.financeAccount.fetch({});
  }, [store.auth.session]);

  const initData = (sr: SupplyReturn) => {
    form.setValues({
      reason: sr.reason,
      status: sr.status,
      payments: [],
    });
  };

  useEffect(() => {
    const id = params.id as string;
    if (id) {
      store.supplyReturn
        .fetchOne(id as string)
        .then((sr) => {
          // load the current stock batch.
          store.supplyReturn.setCurrent(sr);
          initData(sr);
        })
        .catch((error: any) => {
          store.errors.catch(error);
          window.history.back();
        });
    }
  }, [params.id, store.auth.session]);

  if (!supplyReturn) {
    return undefined;
  }
  const getRemainingAmount = () => {
    return (
      supplyReturn.amount -
      form.values.payments
        .map((item: PaymentInfo) => parseInt(item.amount as any))
        .reduce((sum: number, a: number) => sum + a, 0)
    );
  };

  const addPayment = () => {
    form.setValue("payments", [
      ...form.values.payments,
      {
        finance_account_id: "",
        amount: getRemainingAmount(),
        due_date: "",
        payment_date: formatDate(new Date(), "yyyy-MM-dd"),
      },
    ]);
  };

  const submit = async () => {
    store.errors.reset();
    const data = {
      ...unPick(form.values, ["payments"]),
    };

    if (form.values.payments.length) {
      data["payments"] = form.values.payments.map((item: any) => item);
    }

    store.supplyReturn
      .updateCurrent(data)
      .then(() => {
        window.location.reload();
      })
      .catch(store.errors.catch);
  };

  const statusOptions = SupplyReturnStatusActions.filter((item: any) =>
    SupplyReturnValidTransitions[supplyReturn.status]?.includes(item.value)
  );

  return (
    <section>
      <Toast name="clipboard" message="Copy to clipboard" autoClose={2000} />

      <div
        onClick={async () => {
          await navigator.clipboard.writeText(supplyReturn.id);
          store.display.show("clipboard");
        }}
        className="text-xl font-semibold  font-medium hover:text-blue-500 hover:underline text-gray-700 copy-clipboard-parent cursor-pointer mb-3"
      >
        <span> Supply Return ID : </span>
        {supplyReturn.id}
        <button
          className="ml-2 flex-shrink-0"
          onClick={async () => {
            await navigator.clipboard.writeText(supplyReturn.id);
            store.display.show("clipboard");
          }}
        >
          <Copy className="w-4 h-4" />
        </button>
      </div>

      <>{store.errors.render()}</>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <aside className="bg-white rounded-md border border-gray-200 p-6">
          <article className="mt-2">
            <div className="cursor-pointer font-medium  flex items-center gap-3 w-full">
              <Avatar
                className="h-24 w-24 rounded-lg"
                name={supplyReturn.stock_batch?.product.name as string}
                url={
                  supplyReturn.stock_batch?.product.featured_image?.url != null
                    ? supplyReturn.stock_batch.product.featured_image.url
                    : ""
                }
              />
              <div>
                <p className="cursor-pointer">{stock_batch?.product.name}</p>
                <p className="text-gray-600">{stock_batch.batch_code}</p>
                <div className="flex items-center gap-2 text-sm mt-2">
                  <span className="bg-gray-100 border border-gray-300 px-2 py-0.5 rounded-md">
                    {t("Unit Price")}:{" "}
                    {formatAmount(stock_batch?.unit_price || 0)}
                  </span>
                </div>
              </div>
            </div>

            <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 bg-secondary-50/50 p-4 text-sm rounded-md">
              <InfoElement
                detail={{
                  label: t("Quantity to return"),
                  value: formatNumber(supplyReturn.quantity),
                }}
              />
              <InfoElement
                detail={{
                  label: t("Normal Cost"),
                  value: formatAmount(
                    supplyReturn.stock_batch.unit_price * supplyReturn.quantity
                  ),
                }}
              />
              <InfoElement
                detail={{
                  label: t("Amount to refund"),
                  value: formatNumber(supplyReturn.amount),
                }}
              />
            </ul>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextAreaField
                name="reason"
                className="col-span-2"
                value={form.values.reason}
                onChange={form.handleChange}
                label={"Description / Observation"}
                rows={2}
              />
            </div>
          </article>
        </aside>

        <aside className="p-6 bg-white rounded-md border border-gray-200">
          <header className="text-lg font-semibold">Processing</header>
          <article className="mt-2">
            <div className="space-x-4">
              <label className="font-medium text-sm mb-2 block">Status</label>
              <div className="flex items-center gap-4">
                <div
                  className={cn(
                    "rounded-full px-8 w-[150px] py-1 text-sm",
                    SupplyReturnStatusColors[supplyReturn.status]
                  )}
                >
                  {t(SupplyReturnStatusMap[supplyReturn.status])}
                </div>

                {statusOptions.length > 0 && (
                  <LightSelectField
                    name="status"
                    className="w-full"
                    value={form.values.status}
                    onChange={(e: any) => {
                      const value = e.target.value;
                      if (value == "approved") {
                        addPayment();
                      } else {
                        form.setValue("payments", []);
                      }
                      form.setValue("status", value);
                    }}
                    options={statusOptions}
                  />
                )}
              </div>
            </div>
            {form.values.status == "approved" && (
              <>
                <ProcessPayment
                  form={form}
                  net_amount={supplyReturn.amount}
                  allowCashDetails={false}
                />
                <Button
                  onClick={addPayment}
                  className="mt-2 border-none bg-transparent text-primary-500 font-semibold hover:underline hover:bg-transparent px-0"
                >
                  <Plus className="w-4 h-4" />
                  Add Payment
                </Button>
              </>
            )}

            <Show when={supplyReturn.status == "completed"}>
              <PayableTransaction payable={supplyReturn} />
            </Show>
          </article>
        </aside>
      </div>

      {/* Submission Button */}
      <Button className="mt-4 py-2" onClick={submit}>
        {t("Save the changes")}
      </Button>
    </section>
  );
}
